import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STATUS_BAR, STATUS, PRIORITY } from "../constants";
import { Avatar, StatusBadge, PriorityBadge, ProgressBar } from "./ui";
import { fmtDate, parseLocal } from "../utils";
import Icon from "./Icon";

const DAY = 86400000;
const ZOOM_PRESETS = { week: 28, month: 8, quarter: 3 };
const ZOOM_MIN = 2;
const ZOOM_MAX = 40;
const LABEL_W = 184;
const HEADER_H = 26;
const ROW_H = 28;
const BAR_H = 22;
const MINIMAP_H = 20;

function BarPopover({ bar, style, onOpen }) {
  if (!bar) return null;
  const b = bar;
  return (
    <div className="pointer-events-auto absolute z-50 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-xl" style={style}
      onClick={(e) => { e.stopPropagation(); onOpen?.(b.id); }}>
      <div className="mb-1 flex items-center gap-2">
        <StatusBadge status={b.status} />
        <PriorityBadge priority={b.priority} />
      </div>
      <p className="mb-2 text-sm font-semibold uppercase leading-tight">{b.title}</p>
      <div className="mb-2 flex items-center gap-2">
        <ProgressBar value={b.progress_pct} />
        <span className="text-xs font-medium text-slate-500">{b.progress_pct}%</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
        {b.start_date && <span>Inicio: {fmtDate(b.start_date)}</span>}
        {b.due_date && <span>Compromiso: {fmtDate(b.due_date)}</span>}
      </div>
      {b.assignees?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {b.assignees.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 rounded-tag bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              <Avatar name={a.full_name} src={a.avatar_url} size={14} /> {a.full_name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GanttTimeline({ groups, zoom = "week", onOpen }) {
  const scrollRef = useRef(null);
  const minimapRef = useRef(null);
  const [pxDay, setPxDay] = useState(ZOOM_PRESETS[zoom] || 8);
  const [dragging, setDragging] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [popPos, setPopPos] = useState({ top: 0, left: 0 });
  const hoverTimer = useRef(null);

  const showPopover = (bi, e) => {
    clearTimeout(hoverTimer.current);
    const rect = scrollRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left + scrollRef.current.scrollLeft;
    const y = e.clientY - rect.top + scrollRef.current.scrollTop;
    hoverTimer.current = setTimeout(() => {
      setHoverBar(bi.b);
      setPopPos({ top: y - 10, left: Math.min(x, rect.width - 300) });
    }, 300);
  };
  const hidePopover = () => { clearTimeout(hoverTimer.current); setHoverBar(null); };

  useEffect(() => { setPxDay(ZOOM_PRESETS[zoom] || 8); }, [zoom]);

  const zoomIn = () => setPxDay((v) => Math.min(ZOOM_MAX, v + 3));
  const zoomOut = () => setPxDay((v) => Math.max(ZOOM_MIN, v - 3));
  const zoomSlider = (e) => setPxDay(Number(e.target.value));

  const { min, max, days } = useMemo(() => {
    const dates = [];
    groups.forEach((g) => g.bars.forEach((b) => {
      if (b.start_date) dates.push(parseLocal(b.start_date));
      if (b.due_date) dates.push(parseLocal(b.due_date));
    }));
    if (!dates.length) {
      const now = new Date();
      return { min: now, max: new Date(now.getTime() + 30 * DAY), days: 30 };
    }
    let mn = new Date(Math.min(...dates)), mx = new Date(Math.max(...dates));
    mn = new Date(mn.getTime() - 3 * DAY);
    mx = new Date(mx.getTime() + 3 * DAY);
    return { min: mn, max: mx, days: Math.ceil((mx - mn) / DAY) };
  }, [groups]);

  const width = days * pxDay;
  const offset = useCallback((d) => (Math.max(0, (new Date(d) - min)) / DAY) * pxDay, [min, pxDay]);

  const goToday = () => {
    const el = scrollRef.current;
    if (!el) return;
    const todayPx = offset(new Date());
    el.scrollLeft = Math.max(0, todayPx - el.clientWidth / 2 + LABEL_W);
  };

  const { rows, totalH, posById } = useMemo(() => {
    const posById = {};
    const rows = [];
    let y = HEADER_H;
    groups.forEach((g) => {
      const bars = g.bars.map((b) => {
        const start = b.start_date ? parseLocal(b.start_date) : min;
        const end = b.due_date ? parseLocal(b.due_date) : new Date(start.getTime() + 3 * DAY);
        return { b, start, end, left: offset(start), w: Math.max(pxDay, ((end - start) / DAY) * pxDay) };
      });
      bars.forEach((bi, i) => {
        bi.overlap = bars.some((bj, j) => j !== i && bi.start < bj.end && bj.start < bi.end);
        bi.top = y + i * ROW_H;
        if (!(bi.b.id in posById)) {
          posById[bi.b.id] = { xL: LABEL_W + bi.left, xR: LABEL_W + bi.left + bi.w, cy: bi.top + BAR_H / 2 };
        }
      });
      const gh = Math.max(ROW_H, bars.length * ROW_H);
      rows.push({ g, top: y, bars, height: gh });
      y += gh + 6;
    });
    return { rows, totalH: y, posById };
  }, [groups, pxDay, min, offset]);

  const arrows = useMemo(() => {
    const out = [];
    rows.forEach((r) => r.bars.forEach((bi) => {
      (bi.b.depends_on_ids || []).forEach((depId) => {
        const from = posById[depId], to = posById[bi.b.id];
        if (from && to) out.push({ x1: from.xR, y1: from.cy, x2: to.xL, y2: to.cy });
      });
    }));
    return out;
  }, [rows, posById]);

  const months = useMemo(() => {
    const out = [];
    const cur = new Date(min.getFullYear(), min.getMonth(), 1);
    while (cur <= max) { out.push({ left: LABEL_W + offset(cur), label: cur.toLocaleDateString("es-VE", { month: "short", year: "2-digit" }) }); cur.setMonth(cur.getMonth() + 1); }
    return out;
  }, [min, max, offset]);

  const todayLeft = LABEL_W + offset(new Date());
  const fullW = LABEL_W + width;
  const hasOverlap = rows.some((r) => r.bars.some((b) => b.overlap));

  // Auto-center on "today" whenever the layout (data/zoom) changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const todayPx = offset(new Date());
      el.scrollLeft = Math.max(0, todayPx - el.clientWidth / 2 + LABEL_W);
    });
    return () => cancelAnimationFrame(id);
  }, [offset, fullW, totalH]);

  // ---- Minimap ----
  const [viewPort, setViewPort] = useState({ left: 0, width: 100 });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sync = () => {
      const sw = el.scrollWidth || 1;
      setViewPort({ left: (el.scrollLeft / sw) * 100, width: (el.clientWidth / sw) * 100 });
    };
    sync();
    el.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    return () => { el.removeEventListener("scroll", sync); window.removeEventListener("resize", sync); };
  }, [fullW, pxDay]);

  const onMinimapDown = (e) => {
    setDragging(true);
    scrollToMinimap(e);
  };
  const onMinimapMove = (e) => { if (dragging) scrollToMinimap(e); };
  const onMinimapUp = () => setDragging(false);

  useEffect(() => {
    if (!dragging) return;
    const up = () => setDragging(false);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => { window.removeEventListener("mouseup", up); window.removeEventListener("touchend", up); };
  }, [dragging]);

  const scrollToMinimap = (e) => {
    const el = scrollRef.current;
    const mm = minimapRef.current;
    if (!el || !mm) return;
    const rect = mm.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    el.scrollLeft = pct * el.scrollWidth - el.clientWidth / 2;
  };

  // Minimap bars (condensed)
  const minimapBars = useMemo(() => {
    if (!days) return [];
    return rows.flatMap((r) => r.bars.map((bi) => ({
      left: ((bi.left) / width) * 100,
      w: Math.max(0.3, (bi.w / width) * 100),
      color: STATUS_BAR[bi.b.status] || "#94a3b8",
      row: r.g.user_id,
    })));
  }, [rows, width, days]);

  const minimapTodayPct = days ? (offset(new Date()) / width) * 100 : 0;

  return (
    <div className="space-y-2">
      {/* Floating toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={goToday} className="btn-ghost flex items-center gap-1 text-xs" title="Ir a hoy">
          <Icon name="calendar" size={14} /> Hoy
        </button>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 py-1">
          <button onClick={zoomOut} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Alejar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35M8 11h6"/></svg>
          </button>
          <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} value={pxDay} onChange={zoomSlider}
            className="h-1 w-24 cursor-pointer accent-brand-600" title={`Zoom: ${pxDay}px/día`} />
          <button onClick={zoomIn} className="rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700" title="Acercar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35M8 11h6M11 8v6"/></svg>
          </button>
        </div>
        <span className="text-[11px] text-slate-400">
          {pxDay >= 20 ? "Semana" : pxDay >= 6 ? "Mes" : "Trimestre"}
        </span>
        <div className="ml-auto flex flex-wrap gap-3 text-[11px] text-slate-400">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded" style={{ backgroundColor: STATUS_BAR[k] }} />{v.label}</span>
          ))}
          {hasOverlap && <span className="flex items-center gap-1 font-medium text-rose-600"><Icon name="alert" size={12} /> Solape</span>}
        </div>
      </div>

      {/* Main scrollable area */}
      <div ref={scrollRef} className="overflow-x-auto overflow-y-auto rounded-lg border border-slate-200" style={{ maxHeight: "70vh" }}>
        <div className="relative" style={{ width: fullW + 16, minWidth: "100%", height: totalH + 8 }}>
          {/* month header */}
          {months.map((m, i) => (
            <div key={i} className="absolute top-0 border-l border-slate-200 pl-1 text-[11px] text-slate-400" style={{ left: m.left, height: HEADER_H }}>{m.label}</div>
          ))}

          {/* today line */}
          {todayLeft >= LABEL_W && todayLeft <= fullW && (
            <div className="absolute z-10 w-px bg-rose-400" style={{ left: todayLeft, top: 0, height: totalH }}>
              <span className="absolute left-1 top-0 text-[11px] font-semibold text-rose-500">hoy</span>
            </div>
          )}

          {/* Bandas por grupo: líneas horizontales que encierran cada proyecto. */}
          {rows.map((r) => (
            <div key={"band-" + r.g.user_id} className="pointer-events-none absolute left-0 z-0 border-y border-slate-200 bg-slate-50/40"
              style={{ top: r.top - 3, width: fullW, height: r.height + 6 }} />
          ))}

          {/* dependency arrows */}
          <svg className="pointer-events-none absolute inset-0 z-20" width={fullW + 16} height={totalH + 8}>
            <defs>
              <marker id="gt-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#7c8aa0" />
              </marker>
            </defs>
            {arrows.map((a, i) => {
              const midx = (a.x1 + a.x2) / 2;
              return <path key={i} d={`M${a.x1},${a.y1} C${midx},${a.y1} ${midx},${a.y2} ${a.x2},${a.y2}`} fill="none" stroke="#7c8aa0" strokeWidth="1.3" markerEnd="url(#gt-arrow)" opacity="0.8" />;
            })}
          </svg>

          {/* group labels + bars */}
          {rows.map((r) => (
            <div key={r.g.user_id}>
              <div className="absolute flex items-center gap-2" style={{ left: 0, top: r.top, width: LABEL_W - 8, height: BAR_H }}>
                {r.g.is_phase ? <Icon name="flag" size={16} className="text-violet-500" />
                  : r.g.is_project ? <Icon name="folder" size={18} className="text-brand-500" />
                  : <Avatar name={r.g.full_name} size={22} />}
                <span className="truncate text-sm font-semibold">{r.g.full_name}</span>
              </div>
              {r.bars.map((bi) => (
                <div key={bi.b.id + "-" + bi.top} onClick={() => onOpen?.(bi.b.id)}
                  onMouseEnter={(e) => showPopover(bi, e)} onMouseLeave={hidePopover}
                  className={`group absolute z-10 flex cursor-pointer items-center overflow-hidden rounded px-2 text-[11px] font-semibold text-white shadow-sm ring-white/40 transition-all hover:z-20 hover:shadow-pop hover:ring-2 hover:ring-offset-1 ${bi.overlap ? "ring-2 ring-rose-500" : ""}`}
                  style={{ left: LABEL_W + bi.left, top: bi.top, width: bi.w, height: BAR_H, backgroundColor: STATUS_BAR[bi.b.status] || "#94a3b8" }}>
                  {/* progress fill: completed portion reads darker */}
                  {bi.b.progress_pct > 0 && bi.b.status !== "completed" && (
                    <span className="pointer-events-none absolute inset-y-0 left-0 z-0 bg-black/20" style={{ width: `${Math.min(100, bi.b.progress_pct)}%` }} />
                  )}
                  {bi.b.assignees?.length > 0 && (
                    <span className="relative z-10 mr-1 flex -space-x-1.5">
                      {bi.b.assignees.slice(0, 3).map((a) => (
                        <span key={a.id} className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/30 text-[7px] font-bold ring-1 ring-white/50">{a.full_name?.[0]}</span>
                      ))}
                      {bi.b.assignees.length > 3 && <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/30 text-[7px] ring-1 ring-white/50">+{bi.b.assignees.length - 3}</span>}
                    </span>
                  )}
                  <span className="relative z-10 truncate uppercase [text-shadow:0_1px_1px_rgba(0,0,0,0.25)]">{bi.b.title}</span>
                </div>
              ))}
            </div>
          ))}

          {/* Hover popover */}
          {hoverBar && <BarPopover bar={hoverBar} style={popPos} onOpen={onOpen} />}
          {rows.length === 0 && <div className="ml-48 py-12 text-center text-sm text-slate-400">Sin tareas en el rango.</div>}
        </div>
      </div>

      {/* Minimap */}
      {rows.length > 0 && (
        <div
          ref={minimapRef}
          className="relative mt-2 cursor-pointer select-none overflow-hidden rounded border border-slate-200/60 bg-slate-50/50"
          style={{ height: MINIMAP_H }}
          title="Mapa de navegación — arrastra el recuadro azul para moverte por el timeline"
          onMouseDown={onMinimapDown}
          onMouseMove={onMinimapMove}
          onMouseUp={onMinimapUp}
          onTouchStart={onMinimapDown}
          onTouchMove={onMinimapMove}
          onTouchEnd={onMinimapUp}
        >
          {/* minimap bars */}
          {minimapBars.map((b, i) => (
            <div key={i} className="absolute rounded-sm opacity-40" style={{
              left: `${b.left}%`, width: `${b.w}%`, top: 3, height: MINIMAP_H - 6,
              backgroundColor: b.color,
            }} />
          ))}
          {/* today marker */}
          {minimapTodayPct > 0 && minimapTodayPct < 100 && (
            <div className="absolute top-0 w-px bg-rose-400" style={{ left: `${minimapTodayPct}%`, height: MINIMAP_H }} />
          )}
          {/* viewport indicator */}
          <div className="absolute top-0 border-x-2 border-brand-500 bg-brand-500/10" style={{
            left: `${viewPort.left}%`, width: `${Math.max(2, viewPort.width)}%`, height: MINIMAP_H,
          }}>
            <div className="absolute inset-x-0 top-0 h-0.5 bg-brand-500" />
            <div className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-500" />
          </div>
        </div>
      )}
      {rows.length > 0 && (
        <p className="text-center text-[11px] text-slate-400">Mapa de navegación — arrastra el recuadro azul para moverte por el timeline</p>
      )}
    </div>
  );
}
